import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { IDestroyOptions, Sprite, TextStyle, Text } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { GameFont } from "../../../common/GameFont";
import { LeaderboardIntroDescriptionContainer } from "../components/LeaderboardIntroDescriptionContainer";
import { LeaderboardIntroResourcePackage } from "../resources/LeaderboardIntroResourcePackage";
import { DropShadowFilter } from "pixi-filters";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";

export class LeaderboardIntroPopupView extends BaseScalableView<void> {
    private static readonly AMAZING_TEXT = "AMAZING";
    private static readonly REWARDS_TEXT = "REWARDS";
    private static readonly MISS_IT_TEXT = "DON'T MISS IT";
    private static readonly CLICK_ON_TEXT = "CLICK ON";
    private static readonly LEADERBOARD_ICON_TEXT = "LEADERBOARD ICON";
    private static readonly LEARN_MORE_TEXT = "TO LEARN MORE";

    private viewBackground: Sprite;
    private topLight1: Sprite;
    private topLight2: Sprite;
    private leftLight: Sprite;
    private rightLight: Sprite;
    private bottomLight: Sprite;

    private starsBackground: Sprite;
    private boxesBackground: Sprite;
    private logo: Sprite;
    private missItText: Text;
    private clickText: Text;
    private leaderboardIconText: Text;
    private learnMoreText: Text;
    private leaderboardIcon: Sprite;
    private hand: Sprite;
    private amazingText: Text;
    private rewardsText: Text;
    private descriptionContainer: LeaderboardIntroDescriptionContainer;
    private showViewAnimationTimeLine: gsap.core.Timeline;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        private readonly commonResources: CommonResourcesPackage,
        private readonly leaderboardIntroResources: LeaderboardIntroResourcePackage
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.leaderboardIntroResources = leaderboardIntroResources;
        this.showViewAnimationTimeLine = new gsap.core.Timeline({ paused: true });
        this.showViewAnimationTimeLine.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.4, ease: "power1.in" });
        this.build();
        this.eventMode = "static";
        this.on("pointerup", this.gotItClickHandle, this);
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";

        this.topLight1 = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_light_4_png);
        this.topLight2 = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_light_3_png);
        this.bottomLight = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_light_2_png);
        this.leftLight = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_light_1_png);
        this.rightLight = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_light_1_png);
        this.rightLight.scale.x = -1;

        this.starsBackground = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_stars_png);
        this.boxesBackground = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_boxes_png);

        this.missItText = new Text(LeaderboardIntroPopupView.MISS_IT_TEXT, new WhiteTextStyle());
        this.logo = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_logo_png);

        this.clickText = new Text(LeaderboardIntroPopupView.CLICK_ON_TEXT, new WhiteTextStyle());
        this.leaderboardIconText = new Text(LeaderboardIntroPopupView.LEADERBOARD_ICON_TEXT, new OrangeTextStyle());
        this.learnMoreText = new Text(LeaderboardIntroPopupView.LEARN_MORE_TEXT, new WhiteTextStyle());
        this.clickText.style.fontSize = this.learnMoreText.style.fontSize = 36;

        this.leaderboardIcon = new Sprite(this.leaderboardIntroResources.assets_views_leaderboard_intro_leaderboard_icon_png);
        this.leaderboardIcon.pivot.set(this.leaderboardIcon.width * 0.5, this.leaderboardIcon.height * 0.5);

        this.hand = new Sprite(this.commonResources.assets_common_ui_hand_hand_png);
        this.hand.scale.set(-0.6, 0.6);
        this.hand.angle = -73;

        this.amazingText = new Text(LeaderboardIntroPopupView.AMAZING_TEXT, new DescriptionTextStyle());
        this.amazingText.filters = [new DropShadowFilter({ blur: 10, quality: 4, alpha: 1, color: 0xFF6E0E })];

        this.rewardsText = new Text(LeaderboardIntroPopupView.REWARDS_TEXT, new DescriptionTextStyle());
        this.rewardsText.style.fontSize = 195;
        this.rewardsText.filters = [new DropShadowFilter({ blur: 10, quality: 4, alpha: 1, color: 0xFF6E0E })];

        this.descriptionContainer = new LeaderboardIntroDescriptionContainer(this.commonResources, this.leaderboardIntroResources);

        this.updateTextRotation([
            this.missItText,
            this.amazingText,
            this.rewardsText,
            this.clickText,
            this.leaderboardIconText,
            this.learnMoreText
        ]);

        this.addChild(
            this.viewBackground,
            this.topLight1,
            this.topLight2,
            this.bottomLight,
            this.leftLight,
            this.rightLight,
            this.starsBackground,
            this.boxesBackground,
            this.logo,
            this.missItText,
            this.clickText,
            this.leaderboardIconText,
            this.learnMoreText,
            this.leaderboardIcon,
            this.hand,
            this.amazingText,
            this.rewardsText,
            this.descriptionContainer
        );
    }

    private updateTextRotation(texts: Array<Text>): void {
        for (const text of texts) {
            text.angle = -5;
            text.skew.x = -0.08;
        }
    }

    private async gotItClickHandle(): Promise<void> {
        const fullScreenInputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        fullScreenInputBlocker.blocked = true;
        this.addChild(fullScreenInputBlocker);
        await this.hide();
        this.operationControls.complete();
    }

    private async show(): Promise<void> {
        await this.showViewAnimationTimeLine.play();
    }

    private async hide(): Promise<void> {
        await this.showViewAnimationTimeLine.reverse();
    }

    private layout(width: number, height: number): void {
        this.bottomLight.position.set(0, height - this.bottomLight.height);
        this.leftLight.position.set(0, 500);
        this.rightLight.position.set(width, 500);
        this.boxesBackground.position.set(0, height - this.boxesBackground.height);
        this.logo.position.set(122, 160);
        this.missItText.position.set(this.logo.x + this.logo.width - this.missItText.width - 3, this.logo.y - this.missItText.height + 25);
        this.clickText.position.set(this.logo.x, this.logo.y + this.logo.height + 20);
        this.leaderboardIconText.position.set(this.clickText.x + this.clickText.width + 5, this.clickText.y - 9);
        this.learnMoreText.position.set(this.clickText.x, this.clickText.y + this.clickText.height + 3);
        this.leaderboardIcon.position.set(688, 547);
        this.hand.position.set(362, 403);
        this.amazingText.position.set(150, 1343);
        this.rewardsText.position.set(this.amazingText.x, this.amazingText.y + this.amazingText.height - 35);
        this.descriptionContainer.position.set(139, 636);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.gotItClickHandle, this);
        this.showViewAnimationTimeLine?.kill();
        super.destroy(options);
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 207,
            dropShadow: true,
            dropShadowAngle: 0,
            dropShadowBlur: 40,
            dropShadowColor: "#FFC205",
            dropShadowDistance: -3,
            padding: 20,
        });
    }
}

class WhiteTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 65
        });
    }
}

class OrangeTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fill: ["#FFE302", "#FF9024"],
            fillGradientType: 1,
            fillGradientStops: [
                0,
                0.8
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36
        });
    }
}
